import { useCallback, useEffect, useState } from "react";
import { Listing, WithID } from "../../types/db";
import { ListingFEService } from "../../services/listing";
import { StorageListingFEService } from "../../services/storage";
import { DropEvent, FileRejection } from "react-dropzone";
import { isDeepStrictEqual } from "../../types/utils/isDeepEqual";

type FileImage = WithID<Listing['photos_ordered'][number]> & {
    /** The preview url */
    preview: string;
    /** The percentage uploaded */
    percent: number;
    /** The error if issue during upload */
    error?: Error;
}

const mapListingPhoto = (listingPhotos?: Listing['photos_ordered']): FileImage[] => {
    if (!listingPhotos?.length) return [];
    return listingPhotos.map(p => ({
        ...p,
        percent: 100,
        preview: p.url,
    }))
}

export const useListingEditPhotos = (listingId: string, listingPhotos?: Listing['photos_ordered']) => {
    const [files, setFiles] = useState<FileImage[]>(mapListingPhoto(listingPhotos));

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => files.forEach(file => URL.revokeObjectURL(file.preview));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!listingId) return;
        setFiles(files => {
            if (isDeepStrictEqual(files, mapListingPhoto(listingPhotos))) {
                return files;
            } else {
                return mapListingPhoto(listingPhotos)
            }
        })
    }, [listingId, listingPhotos]);

    const onDrop: (<T extends File>(acceptedFiles: T[], fileRejections: FileRejection[], event: DropEvent) => void)
        = useCallback((acceptedFiles) => {
            acceptedFiles.forEach((acceptedFile) => {
                if (!listingId) return;
                StorageListingFEService(listingId)
                    .uploadForListing(
                        acceptedFile,
                        acceptedFile.name,
                        (percent, finalInfo, error) => {
                            if (!!error) {
                                setFiles(files => files.map((file) => {
                                    if (file.name === acceptedFile.name) {
                                        return {
                                            ...file,
                                            percent: 0,
                                            error: error,
                                        }
                                    }
                                    return file;
                                }));
                            } else if (percent < 100) {
                                setFiles(files => files.map((file) => {
                                    if (file.name === acceptedFile.name) {
                                        return {
                                            ...file,
                                            percent,
                                        }
                                    }
                                    return file;
                                }));
                            } else if (!!finalInfo) {
                                setFiles(files => files.map((file) => {
                                    if (file.name === acceptedFile.name) {
                                        return {
                                            ...file,
                                            ...finalInfo,
                                            preview: finalInfo.url,
                                            percent: 100,
                                        }
                                    }
                                    return file;
                                }));
                            }
                        })
                    .then((finalInfo) => {
                        setFiles(files => files.map((file) => {
                            if (file.name === acceptedFile.name) {
                                return {
                                    ...file,
                                    ...finalInfo,
                                    preview: finalInfo.url,
                                    percent: 100,
                                }
                            }
                            return file;
                        }));
                    })
                    .catch((error) => {
                        setFiles(files => {
                            // console.warn(error, files, acceptedFiles);
                            return files.map((file) => {
                                if (file.name === acceptedFile.name) {
                                    // console.warn('change', file, error);
                                    return {
                                        ...file,
                                        error,
                                        percent: 100,
                                    }
                                }
                                return file;
                            })
                        });
                    });
            });

            setFiles(files => ([
                ...files,
                ...acceptedFiles.map(file => ({
                    preview: URL.createObjectURL(file),
                    name: file.name,
                    description: '',
                    id: '',
                    url: '',
                    percent: 0,
                }))]));
        }, [listingId]);


    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const handleDragStart: (index: number) => React.DragEventHandler<HTMLDivElement> = useCallback((index: number) => () => {
        // console.log('handle drag start', index)
        setDraggedIndex(index);
    }, []);

    const handleDragOver: React.DragEventHandler<HTMLDivElement> = useCallback((e) => {
        // console.log('handle drag over')
        e.preventDefault(); // Necessary to allow drop
    }, []);

    const handleDrop: (index: number) => React.DragEventHandler<HTMLDivElement> = useCallback((dropIndex: number) => () => {
        // console.log('handle drop', draggedIndex, dropIndex)
        if (draggedIndex === null) return;

        const items = Array.from(files);
        const [draggedItem] = items.splice(draggedIndex, 1);
        items.splice(dropIndex, 0, draggedItem);

        setFiles(items);
        setDraggedIndex(null);
    }, [draggedIndex, files]);

    const handleDragEnd: React.DragEventHandler<HTMLDivElement> = useCallback(() => {
        // console.log('handle drag end')
        setDraggedIndex(null);
    }, []);


    const handleDeletePicture = useCallback((file: FileImage) => {
        // console.log('delete photo file', file)
        if (!!listingId
            && file.percent === 100) {
            new ListingFEService()
                .listingPhotosService(listingId)
                .delete(file.id)//trigger will take care of storage deletion
            setFiles(files => files.filter(f => f.name !== file.name))
        }
    }, [listingId])

    const handleFileOrderTop = useCallback((file: FileImage) => {
        setFiles(files => {
            const fileIndex = files.findIndex(f => f.id === file.id);
            if (fileIndex === -1) {
                //if not found
                return files
            }
            if (fileIndex === 0) {
                //if already at the full top
                // console.log('already top')
                return files
            }
            //delete the file at is original position
            files.splice(fileIndex, 1)
            //put back the file all the way on top
            files.splice(0, 0, file)
            //return the changed files array
            return [...files];//force rerender
        })
    }, []);

    const handleFileOrderUp = useCallback((file: FileImage) => {
        setFiles(files => {
            const fileIndex = files.findIndex(f => f.id === file.id);
            if (fileIndex === -1) {
                //if not found
                return files
            }
            if (fileIndex === 0) {
                //if already at the full top
                // console.log('already top')
                return files
            }
            //delete the file at is original position
            files.splice(fileIndex, 1)
            //put back the file one index 'up'
            files.splice(fileIndex - 1, 0, file)
            //return the changed files array
            return [...files];//force rerender
        })
    }, []);


    const handleFileOrderDown = useCallback((file: FileImage) => {
        setFiles(files => {
            const fileIndex = files.findIndex(f => f.id === file.id);
            if (fileIndex === -1) {
                //if not found
                return files
            }
            if (fileIndex === files.length - 1) {
                //if already at the full bottom
                // console.log('already bottom')
                return files;
            }

            //delete the file at is original position
            files.splice(fileIndex, 1)
            //put back the file one index 'down'
            files.splice(fileIndex + 1, 0, file)
            //return the changed files array
            return [...files];
        })
    }, []);

    const handleFileOrderBottom = useCallback((file: FileImage) => {
        setFiles(files => {
            const fileIndex = files.findIndex(f => f.id === file.id);
            if (fileIndex === -1) {
                //if not found
                return files
            }
            if (fileIndex === files.length - 1) {
                //if already at the full bottom
                // console.log('already bottom')
                return files;
            }

            //delete the file at is original position
            files.splice(fileIndex, 1)
            //put back the file all the way to the bottom
            files.splice(files.length - 1, 0, file)
            //return the changed files array
            return [...files];
        })
    }, []);

    const handleFileDescriptionChanged = useCallback((file: FileImage, newDescription: string) => {
        setFiles(files => {
            const fileIndex = files.findIndex(f => f.id === file.id);
            if (fileIndex === -1) {
                //if not found
                return files
            }
            //put the new description in the file
            files[fileIndex].description = newDescription;
            return [...files];
        })
    }, [])

    const dragStyle = useCallback((index: number) => {
        return {
            opacity: draggedIndex === index ? 0.5 : 1,
            cursor: 'grab',
            transition: 'opacity 0.2s',
        }
    }, [draggedIndex])

    return {
        photos: files,
        onDropPhotos: onDrop,
        deletePhoto: handleDeletePicture,
        orderPhotoToTop: handleFileOrderTop,
        orderPhotoUp: handleFileOrderUp,
        orderPhotoDown: handleFileOrderDown,
        orderPhotoToBottom: handleFileOrderBottom,
        changePhotoDescription: handleFileDescriptionChanged,
        onDragFileStart: handleDragStart,
        onDragFileEnd: handleDragEnd,
        onDragFileOver: handleDragOver,
        onDragFileDrop: handleDrop,
        onDrageStyleFn: dragStyle
    }
}